import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { GuestBottomNav } from '../components/GuestBottomNav';
import NotificationBellButton from '../components/NotificationBellButton';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { hostCardStyle } from '../components/HostSettingsCard';
import { getInvite } from '../utils/inviteApi';
import { clearActiveGuestInviteId } from '../utils/deepLinkHandler';
import { formatEuropeanDate } from '../utils/dateFormat';
import { feedbackInviteOk } from '../utils/appFeedback';

const fontFamily = "'Inria Serif', serif";

export default function GuestInviteWaitingPage() {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [invite, setInvite] = useState(null);

  useEffect(() => {
    let timer;
    const pollInvite = async () => {
      try {
        const res = await getInvite(inviteId);
        setInvite(res);
      } catch (err) {
        toast.error('Invite not found');
      }
    };
    pollInvite();
    timer = setInterval(pollInvite, 4000);
    return () => clearInterval(timer);
  }, [inviteId]);

  const conversationId = location.state?.conversationId || invite?.conversation_id;
  const autoLaunchQr = Boolean(location.state?.autoLaunchQr);
  const status = invite?.status || 'pending_host_approval';
  const isWaiting = status === 'pending_host_approval';
  const isApproved = status === 'approved';
  const isDeclined = ['declined', 'declined_by_host', 'cancelled_by_ical'].includes(status);
  const isDirectAcceptFlow = invite?.source === 'ical';
  const listingImage = invite?.listing_image || '/allure-logo-mono.png';
  const statusTitle = isWaiting
    ? 'Waiting for approval'
    : isApproved
      ? (autoLaunchQr ? 'Invitation accepted' : 'Access confirmed')
      : status === 'cancelled_by_ical'
        ? 'Reservation no longer available'
        : 'Access declined';
  const statusDescription = isWaiting
    ? 'Your host is reviewing your request. This usually takes just a moment.'
    : isApproved
      ? (autoLaunchQr ? 'Your stay is approved. We’re opening your QR access now.' : 'Your Allure QR code is now active and ready to use.')
      : status === 'cancelled_by_ical'
        ? 'This stay reopened on Airbnb, so the invitation is no longer active.'
        : 'Your request was declined by the host.';
  const primaryButtonLabel = isApproved ? 'Show QR code' : status === 'cancelled_by_ical' ? 'Back to QR' : 'Retry';

  useEffect(() => {
    if (!autoLaunchQr || !isApproved) {
      return undefined;
    }
    // Fire the celebration chime + haptic in perfect sync with the green ✓
    // badge's mount animation, right before we auto-open the QR page.
    feedbackInviteOk();
    const timer = setTimeout(() => {
      clearActiveGuestInviteId();
      navigate('/guest/qr', { replace: true });
    }, 1450);
    return () => clearTimeout(timer);
  }, [autoLaunchQr, isApproved, navigate]);

  const handlePrimaryAction = () => {
    if (isApproved) {
      clearActiveGuestInviteId();
      navigate('/guest/qr');
      return;
    }

    clearActiveGuestInviteId();
    navigate('/guest/qr');
  };

  return (
    <div className="app-shell" data-testid="guest-invite-waiting-page" style={{ backgroundColor: 'var(--surface)' }}>
      <div style={{ padding: '16px 20px 0', display: 'flex', justifyContent: 'flex-end' }}>
        <NotificationBellButton buttonTestId="guest-waiting-bell-button" popupTestId="guest-waiting-notification-popup" />
      </div>
      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 0 }}>
        <RouteTransitionLogo width={118} height={102} dataTestId="guest-invite-waiting-shared-logo" />
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.34, delay: 0.04, ease: 'easeOut' }} data-testid="guest-waiting-eyebrow" style={{ fontFamily, fontSize: 15, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-2)', textAlign: 'center', margin: '8px 0 0' }}>
          Guest invite
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.34, delay: 0.08, ease: 'easeOut' }} data-testid="guest-waiting-title" style={{ fontFamily, fontSize: 27, fontWeight: 700, color: 'var(--text)', textAlign: 'center', marginTop: 14, lineHeight: 1.08, maxWidth: 320 }}>{statusTitle}</motion.h1>
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.34, delay: 0.12, ease: 'easeOut' }} data-testid="guest-waiting-subtitle" style={{ fontFamily, fontSize: 18, fontWeight: 700, color: 'var(--text)', textAlign: 'center', lineHeight: 1.28, marginTop: 12, maxWidth: 326 }}>{statusDescription}</motion.p>

        {invite ? (
          <motion.div className="premium-card" data-testid="guest-waiting-status-card" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.38, delay: 0.12, ease: 'easeOut' }} style={{ width: '100%', marginTop: 24, borderRadius: 28, padding: '18px 18px 20px' }}>
            <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 10', borderRadius: 24, overflow: 'hidden', background: 'var(--surface-2)' }}>
              <img src={listingImage} alt={invite.listing_name} data-testid="guest-waiting-listing-image" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: isWaiting ? 0.74 : 1 }} />
              <div style={{ position: 'absolute', inset: 0, background: isWaiting ? 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(0,0,0,0.28))' : 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(0,0,0,0.2))' }} />
              {isWaiting ? (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1.1, ease: 'linear' }} data-testid="guest-waiting-loading-ring" style={{ width: 78, height: 78, borderRadius: '50%', border: '8px solid rgba(255,255,255,0.55)', borderTopColor: '#E50000' }} />
                </div>
              ) : (
                <div style={{ position: 'absolute', top: 20, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                  <motion.div initial={{ scale: 0.62, opacity: 0 }} animate={{ scale: [0.62, 1.06, 1], opacity: 1 }} transition={{ duration: 0.68, ease: 'easeOut' }} data-testid="guest-waiting-status-badge" style={{ width: 124, height: 68, borderRadius: 9999, background: isApproved ? '#11B539' : '#FF4A4A', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: isApproved ? '0 14px 24px rgba(17, 181, 57, 0.24)' : '0 14px 24px rgba(255, 74, 74, 0.18)' }}>
                    <span style={{ fontFamily, fontSize: 58, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{isApproved ? '✓' : '✕'}</span>
                  </motion.div>
                </div>
              )}
            </div>
            <p data-testid="guest-waiting-listing-name" style={{ fontFamily, fontSize: 27, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: '18px 0 0', lineHeight: 1.08 }}>{invite.listing_name}</p>
            <div className="premium-card-compact premium-card-nested" data-testid="guest-waiting-date-card" style={{ marginTop: 16, borderRadius: 22, padding: '14px 16px', textAlign: 'center' }}>
              <p style={{ fontFamily, fontSize: 13, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-2)', margin: 0 }}>Stay window</p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 10 }}>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <p style={{ fontFamily, fontSize: 11, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-2)', margin: 0 }}>Check-in</p>
                  <p data-testid="guest-waiting-date-range" style={{ fontFamily, fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: '3px 0 0', lineHeight: 1.1, whiteSpace: 'nowrap' }}>{formatEuropeanDate(invite.check_in_date)}</p>
                </div>
                <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--card-border)', margin: '0 4px' }} />
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <p style={{ fontFamily, fontSize: 11, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-2)', margin: 0 }}>Checkout</p>
                  <p style={{ fontFamily, fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: '3px 0 0', lineHeight: 1.1, whiteSpace: 'nowrap' }}>{formatEuropeanDate(invite.checkout_date)}</p>
                </div>
              </div>
            </div>
            <div style={{ ...hostCardStyle, width: '100%', marginTop: 16, background: 'var(--surface)', borderRadius: 22, padding: '16px 18px' }}>
              <p data-testid="guest-waiting-status-copy" style={{ fontFamily, fontSize: 18, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: 0, lineHeight: 1.28 }}>{isApproved ? (isDirectAcceptFlow ? 'Your QR code is live and will open automatically.' : 'Your Allure access is now active. Tap below to view your QR code.') : isDeclined ? (status === 'cancelled_by_ical' ? 'This reservation changed, so you will need a new invite if your stay returns.' : 'If your check-in details change, ask your host for a fresh invitation link.') : 'You can contact your host while you wait if you need anything.'}</p>
            </div>
          </motion.div>
        ) : null}

        {isWaiting ? (
          <motion.button whileTap={{ scale: 0.985 }} onClick={() => conversationId ? navigate(`/guest/chat/${conversationId}`) : navigate('/guest/share')} data-testid="guest-waiting-contact-host-button" className="premium-pill-button" style={{ marginTop: 28, width: '100%', maxWidth: 280, padding: '13px 0', fontFamily, fontSize: 20, cursor: 'pointer' }}>Contact Host</motion.button>
        ) : autoLaunchQr && isApproved ? null : (
          <motion.button whileTap={{ scale: 0.985 }} onClick={handlePrimaryAction} data-testid="guest-waiting-primary-button" style={{ marginTop: 24, width: '100%', maxWidth: 280, padding: '13px 0', borderRadius: 9999, border: isApproved ? 'none' : '1px solid var(--card-border)', background: isApproved ? 'var(--accent-purple)' : 'var(--surface)', color: isApproved ? '#FFFFFF' : 'var(--text)', boxShadow: 'var(--premium-shadow-main)', fontFamily, fontSize: 20, fontWeight: 700, cursor: 'pointer' }}>{primaryButtonLabel}</motion.button>
        )}

        {autoLaunchQr && isApproved ? <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, delay: 0.25 }} data-testid="guest-waiting-auto-launch-copy" style={{ fontFamily, fontSize: 16, color: 'var(--text-2)', textAlign: 'center', marginTop: 20 }}>Opening your QR access…</motion.p> : null}
      </div>

      <GuestBottomNav />
    </div>
  );
}